import { useSalaryHistogram } from '@/modules/salary/histogram/use-salary-histogram'
import Script from 'next/script'
import { useSalaries } from '@/modules/salary/common/use-salaries'
import { useSalaryParams } from '@/modules/salary/common/use-salary-params'
import { SALARY_FULL_TIME_VALUE } from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import { useSearchParams } from 'next/navigation'
import { SalaryFilterParam } from '@/modules/salary/constants'
import { SalaryPayPeriod } from '@/modules/salary/table/constants'
import { getStateSalaryFiltersFromUrl } from './filters/filter-utils'
import { capitalizeAllWords } from '@/lib/strings'
import { getSearchParamValue } from './common/utils'

interface MonetaryAmountDistribution {
  '@type': 'MonetaryAmountDistribution'
  name: 'total' | 'base' | 'bonus' | 'other_income'
  currency: string
  duration: string
  median: number
  percentile10?: number
  percentile25?: number
  percentile75?: number
  percentile90?: number
}

interface StructuredData {
  '@context': string
  '@type': string
  name: string
  estimatedSalary: MonetaryAmountDistribution[]
  occupationLocation: {
    '@type': string
    name: string
  }
  mainEntityOfPage: {
    '@type': string
    lastReviewed: string
  }
  sampleSize?: number
}

interface HistogramPercentile {
  count: number
  percentile?: string
  salary?: number
}

export function SeoStructuredModules() {
  const { jobType, location: state, occupationSlug } = useSalaryParams()
  const searchParams = useSearchParams()
  const { data } = useSalaryHistogram()
  const { data: salaryData } = useSalaries()
  const stateLocation = getStateSalaryFiltersFromUrl({
    location: state,
  })
  const payPeriod = searchParams.get(SalaryFilterParam.payPeriod)

  if (
    !salaryData?.searchSalary?.data?.length ||
    jobType !== SALARY_FULL_TIME_VALUE ||
    payPeriod === SalaryPayPeriod.Hourly
  )
    return null

  const occupationName = capitalizeAllWords(
    getSearchParamValue(occupationSlug)?.replaceAll('-', ' ') || '',
  )

  // Get location information
  const location = stateLocation?.label
    ? {
        '@type': 'State',
        name: `${stateLocation.label}`,
      }
    : {
        '@type': 'Country',
        name: 'US',
      }

  // Create the structured data object
  const structuredData: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: occupationName,
    estimatedSalary: [],
    occupationLocation: location,
    mainEntityOfPage: {
      '@type': 'WebPage',
      lastReviewed: data?.getHistogram?.updatedAt || new Date().toISOString(),
    },
  }

  // Add sample size if available
  if (data?.getHistogram?.salaryCount) {
    structuredData.sampleSize = data.getHistogram.salaryCount
  }

  // Add total compensation data
  if (data?.getHistogram?.avgTotalComp) {
    const totalComp: MonetaryAmountDistribution = {
      '@type': 'MonetaryAmountDistribution',
      name: 'total',
      currency: 'USD',
      duration: 'P1Y',
      median: data.getHistogram.avgTotalComp,
    }
    const tenPercentile = data?.getHistogram?.histogram?.find(
      (percentile: HistogramPercentile) =>
        percentile.percentile?.includes('p10th'),
    )
    const twentyfivePercentile = data?.getHistogram?.histogram?.find(
      (percentile: HistogramPercentile) =>
        percentile.percentile?.includes('p25th'),
    )
    const seventyFivePercentile = data?.getHistogram?.histogram?.find(
      (percentile: HistogramPercentile) =>
        percentile.percentile?.includes('p75th'),
    )
    const ninetyPercentile = data?.getHistogram?.histogram?.find(
      (percentile: HistogramPercentile) =>
        percentile.percentile?.includes('p90th'),
    )
    if (tenPercentile) {
      totalComp['percentile10'] = tenPercentile.salary
    }
    if (twentyfivePercentile) {
      totalComp['percentile25'] = twentyfivePercentile.salary
    }
    if (seventyFivePercentile) {
      totalComp['percentile75'] = seventyFivePercentile.salary
    }
    if (ninetyPercentile) {
      totalComp['percentile90'] = ninetyPercentile.salary
    }
    structuredData.estimatedSalary.push(totalComp)
  }

  // Add base salary data if available
  if (data?.getHistogram?.baseSalary) {
    const baseSalary: MonetaryAmountDistribution = {
      '@type': 'MonetaryAmountDistribution',
      name: 'base',
      currency: 'USD',
      duration: 'P1Y',
      median: data.getHistogram.baseSalary,
    }

    structuredData.estimatedSalary.push(baseSalary)
  }

  // Add bonus data if available
  if (data?.getHistogram?.bonuses) {
    const bonus: MonetaryAmountDistribution = {
      '@type': 'MonetaryAmountDistribution',
      name: 'bonus',
      currency: 'USD',
      duration: 'P1Y',
      median: data.getHistogram.bonuses,
    }

    structuredData.estimatedSalary.push(bonus)
  }

  // Add other income data if available
  if (data?.getHistogram?.otherIncome) {
    const otherIncome: MonetaryAmountDistribution = {
      '@type': 'MonetaryAmountDistribution',
      name: 'other_income',
      currency: 'USD',
      duration: 'P1Y',
      median: data.getHistogram.otherIncome,
    }

    structuredData.estimatedSalary.push(otherIncome)
  }
  // Only render if we have at least one estimatedSalary entry
  if (structuredData.estimatedSalary.length === 0) {
    return null
  }

  return (
    <Script
      id="salary-structured-data"
      type="application/ld+json"
      data-testid="salary-structured-data"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  )
}
